/**
 * Data Service
 *
 * Microservice for managing user data operations
 * Handles bulk data operations like resetting all user data
 */

import { supabase, getCurrentUserId } from './supabaseClient';
import { deleteAllAIInsightsData } from './aiDataCleanup';

/**
 * Delete all subscription-related data for the current user
 * This includes:
 * - Subscriptions
 * - Price events
 * - Billing history
 * - Analytics cache
 * - User settings (will be auto-detected on resync)
 * - AI Insights data (savings events and usage checks)
 *
 * Note: User account, authentication, preferences, and Gmail connections are preserved
 * Use the "Remove" button on individual Gmail accounts to disconnect them
 *
 * @returns {Promise<Object>} Result object with deletion counts
 */
export const resetAllUserData = async () => {
  try {
    const userId = await getCurrentUserId();
    const results = {
      subscriptionEvents: 0,
      priceEvents: 0,
      billingHistory: 0,
      subscriptions: 0,
      analyticsCache: 0,
      userSettings: 0,
      aiInsights: {
        savingsEvents: 0,
        usageChecks: 0
      }
    };


    // 1. Get subscription IDs first (needed for child table deletion)
    const { data: userSubs } = await supabase
      .from('subscriptions')
      .select('id')
      .eq('user_id', userId);

    const subIds = (userSubs || []).map(s => s.id);

    // 2. Delete child tables FIRST (they reference subscriptions via FK)
    if (subIds.length > 0) {
      // Delete subscription_events
      const { data: deletedSubEvents, error: subEventsError } = await supabase
        .from('subscription_events')
        .delete()
        .in('subscription_id', subIds)
        .select('id');

      if (subEventsError) {
        console.error('Error deleting subscription events:', subEventsError);
      }
      results.subscriptionEvents = deletedSubEvents?.length || 0;

      // Delete price events
      const { data: deletedPriceEvents, error: priceEventsError } = await supabase
        .from('price_events')
        .delete()
        .in('subscription_id', subIds)
        .select('id');

      if (priceEventsError) {
        console.error('Error deleting price events:', priceEventsError);
      }
      results.priceEvents = deletedPriceEvents?.length || 0;

      // Delete billing history
      const { data: deletedBilling, error: billingError } = await supabase
        .from('billing_history')
        .delete()
        .in('subscription_id', subIds)
        .select('id');

      if (billingError) {
        console.error('Error deleting billing history:', billingError);
      }
      results.billingHistory = deletedBilling?.length || 0;

      // Delete savings_events
      const { error: savingsEvtError } = await supabase
        .from('savings_events')
        .delete()
        .in('subscription_id', subIds);

      if (savingsEvtError) {
        console.error('Error deleting savings events (by sub):', savingsEvtError);
      } else {
      }

      // Delete subscription_usage_checks
      const { error: usageError } = await supabase
        .from('subscription_usage_checks')
        .delete()
        .in('subscription_id', subIds);

      if (usageError) {
        console.error('Error deleting usage checks:', usageError);
      } else {
      }
    }

    // 3. Delete dismissed email IDs cache (so re-sync will re-classify everything)
    const { data: deletedDismissed, error: dismissedError } = await supabase
      .from('dismissed_email_ids')
      .delete()
      .eq('user_id', userId)
      .select('id');

    if (dismissedError) {
      console.error('Error deleting dismissed email IDs:', dismissedError);
    } else {
    }

    // 4. Now safe to delete subscriptions (child records already gone)
    const { data: deletedSubs, error: subsError } = await supabase
      .from('subscriptions')
      .delete()
      .eq('user_id', userId)
      .select('id');

    if (subsError) {
      console.error('Error deleting subscriptions:', subsError);
      throw subsError;
    }

    results.subscriptions = deletedSubs?.length || 0;

    // Delete analytics cache
    const { data: deletedAnalytics, error: analyticsError } = await supabase
      .from('analytics_cache')
      .delete()
      .eq('user_id', userId)
      .select('id');

    if (analyticsError) {
      console.error('Error deleting analytics cache:', analyticsError);
      // Don't throw, continue with other deletions
    }

    results.analyticsCache = deletedAnalytics?.length || 0;

    // Delete user settings (will be auto-detected on next load)
    const { data: deletedSettings, error: settingsError } = await supabase
      .from('user_settings')
      .delete()
      .eq('user_id', userId)
      .select('id');

    if (settingsError) {
      console.error('Error deleting user settings:', settingsError);
      // Don't throw, continue with other deletions
    } else {
      results.userSettings = deletedSettings?.length || 0;
    }

    // Delete AI Insights data (savings events and usage checks)
    try {
      const aiCleanupResult = await deleteAllAIInsightsData();
      if (aiCleanupResult.success) {
        results.aiInsights = aiCleanupResult.results;
      }
    } catch (aiError) {
      console.error('Error deleting AI insights data:', aiError);
      // Don't throw - AI data is optional
    }

    // Reset subscription counts for all Gmail accounts to 0
    try {
      const { error: resetCountError } = await supabase
        .from('synced_gmail_accounts')
        .update({ subscription_count: 0 })
        .eq('user_id', userId);

      if (resetCountError) {
        console.error('Error resetting Gmail account subscription counts:', resetCountError);
      } else {
      }
    } catch (resetError) {
      console.error('Error resetting subscription counts:', resetError);
      // Don't throw - continue with completion
    }

    return {
      success: true,
      results
    };

  } catch (error) {
    console.error('resetAllUserData error:', error);
    throw error;
  }
};

/**
 * Get data statistics for the current user
 * Useful for showing user what will be deleted
 *
 * @returns {Promise<Object>} Object with counts of each data type
 */
export const getUserDataStats = async () => {
  try {
    const userId = await getCurrentUserId();
    const stats = {
      subscriptions: 0,
      priceEvents: 0,
      billingHistory: 0,
      analyticsCache: 0
    };

    // Count subscriptions
    const { count: subsCount, error: subsError } = await supabase
      .from('subscriptions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!subsError) {
      stats.subscriptions = subsCount || 0;
    }

    // Count price events
    const { count: eventsCount, error: eventsError } = await supabase
      .from('price_events')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!eventsError) {
      stats.priceEvents = eventsCount || 0;
    }

    // Count billing history
    const { count: billingCount, error: billingError } = await supabase
      .from('billing_history')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!billingError) {
      stats.billingHistory = billingCount || 0;
    }

    // Count analytics cache
    const { count: analyticsCount, error: analyticsError } = await supabase
      .from('analytics_cache')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId);

    if (!analyticsError) {
      stats.analyticsCache = analyticsCount || 0;
    }

    return stats;

  } catch (error) {
    console.error('getUserDataStats error:', error);
    throw error;
  }
};

export default {
  resetAllUserData,
  getUserDataStats
};
